<template>
  <div v-if="isMultiEvents" class="multi-event-wrapper">
    <!-- Header -->
    <div class="multi-event-header">
      <div class="event-count-badge">
        <v-icon small color="white" class="ml-1"
          >mdi-alert-circle-outline</v-icon
        >
        <span>{{ events.length }} אירועים פתוחים</span>
      </div>
      <span class="event-counter"
        >{{ eventIndex + 1 }} / {{ events.length }}</span
      >
    </div>

    <!-- Carousel -->
    <div class="carousel-row">
      <v-btn icon class="nav-arrow" @click="backEvent">
        <v-icon>mdi-chevron-right</v-icon>
      </v-btn>

      <div
        class="carousel-track"
        @touchstart="onSwipeStart"
        @touchend="onSwipeEnd"
        @mousedown="onSwipeStart"
        @mouseup="onSwipeEnd"
      >
        <transition :name="slideDirection" mode="out-in">
          <event-bubble
            :key="eventIndex"
            :event="event"
            :btnText="isAdminDashboard ? 'סיום אירוע' : 'שליחת דוח מצב'"
            :allowEdit="isAdminDashboard"
            :showActionBtn="showActionButton"
            @actionBtnClick="actionBtnClick"
            @actionEdit="actionEdit"
          />
        </transition>
      </div>

      <v-btn icon class="nav-arrow" @click="nextEvent">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
    </div>

    <!-- Dot indicators -->
    <div class="dot-track">
      <button
        v-for="(e, i) in events"
        :key="i"
        class="dot-btn"
        :class="{ active: i === eventIndex }"
        @click="goToEvent(i)"
        :aria-label="'אירוע ' + (i + 1)"
      >
        <span class="dot-fill"></span>
      </button>
    </div>
  </div>

  <event-bubble
    v-else-if="event"
    :event="event"
    :btnText="isAdminDashboard ? 'סיום אירוע' : 'שליחת דוח מצב'"
    :allowEdit="isAdminDashboard"
    :showActionBtn="showActionButton"
    @actionBtnClick="actionBtnClick"
    @actionEdit="actionEdit"
  />
  <div v-else></div>
</template>

<script>
import EventBubble from './EventBubble.vue'
export default {
  name: 'multi-event',
  components: { EventBubble },
  props: { showActionBtn: { type: Boolean, default: false } },
  data() {
    return {
      eventIndex: 0,
      slideDirection: 'slide-right',
      swipeStartX: 0,
    }
  },
  computed: {
    isAdminDashboard() {
      return this.$route.name === 'DashboardAdmin'
    },
    showActionButton() {
      return this.showActionBtn || this.isAdminDashboard
    },
    events() {
      return this.$store.getters.events
    },
    event() {
      return this.$store.getters.event
    },
    isMultiEvents() {
      return this.events.length > 1
    },
    leftEvent() {
      const index = this.eventIndex ? this.eventIndex : this.events.length
      return this.events[index - 1]
    },
    rightEvent() {
      const index =
        this.eventIndex === this.events.length - 1 ? -1 : this.eventIndex
      return this.events[index + 1]
    },
  },
  methods: {
    actionBtnClick() {
      this.$emit('actionBtnClick')
    },
    actionEdit() {
      this.$emit('actionEdit')
    },
    nextEvent() {
      this.slideDirection = 'slide-right'
      this.eventIndex =
        this.eventIndex === this.events.length - 1 ? 0 : this.eventIndex + 1
    },
    backEvent() {
      this.slideDirection = 'slide-left'
      this.eventIndex =
        this.eventIndex === 0 ? this.events.length - 1 : this.eventIndex - 1
    },
    goToEvent(index) {
      this.slideDirection =
        index > this.eventIndex ? 'slide-right' : 'slide-left'
      this.eventIndex = index
    },
    onSwipeStart(e) {
      this.swipeStartX = e.touches ? e.touches[0].clientX : e.clientX
    },
    onSwipeEnd(e) {
      const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX
      const diff = endX - this.swipeStartX
      if (Math.abs(diff) < 50) return
      diff > 0 ? this.nextEvent() : this.backEvent()
    },
  },
  mounted() {
    if (this.events.length === 1) {
      this.$store.commit('event/set', this.events[0])
    }
  },
  watch: {
    eventIndex() {
      this.$store.commit('event/set', this.events[this.eventIndex])
    },
  },
}
</script>

<style lang="scss" scoped>
.multi-event-wrapper {
  width: 100%;
}

/* ---- Header ---- */
.multi-event-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 5%;
  margin-bottom: 8px;
}

.event-count-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--primary);
  color: white;
  font-size: 13px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  animation: gentle-pulse 2.5s ease-in-out infinite;
}

@keyframes gentle-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.75;
  }
}

.event-counter {
  font-size: 14px;
  font-weight: 600;
  color: var(--secondary);
  letter-spacing: 1px;
}

/* ---- Carousel ---- */
.carousel-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.carousel-track {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  cursor: grab;
  user-select: none;
}

.nav-arrow {
  flex-shrink: 0;
  color: var(--primary) !important;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.15);
  }

  .v-icon {
    font-size: 28px !important;
  }
}

/* ---- Slide transitions ---- */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.3s ease;
}

.slide-left-enter {
  transform: translateX(-30px);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(30px);
  opacity: 0;
}

.slide-right-enter {
  transform: translateX(30px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(-30px);
  opacity: 0;
}

/* ---- Dot indicators ---- */
.dot-track {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 6px;
  margin: 12px auto 4px;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 20px;
  padding: 6px 14px;
  width: fit-content;
}

.dot-btn {
  position: relative;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  .dot-fill {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--lightGrey);
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  }

  &.active .dot-fill {
    width: 26px;
    height: 8px;
    border-radius: 10px;
    background: var(--primary);
    box-shadow: 0 0 8px rgba(243, 48, 21, 0.4);
  }

  &:hover:not(.active) .dot-fill {
    background: var(--orange);
    transform: scale(1.3);
  }
}
</style>
